import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import authService from '../services/auth-service';
import { trackAuth, trackConversion, trackGoogleLogin } from '../utils/gtm';

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth action types
const AuthActionTypes = {
  AUTH_START: 'AUTH_START',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_ERROR: 'AUTH_ERROR',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  AUTH_CLEAR_ERROR: 'AUTH_CLEAR_ERROR',
  AUTH_UPDATE_USER: 'AUTH_UPDATE_USER',
  AUTH_INITIALIZE: 'AUTH_INITIALIZE',
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActionTypes.AUTH_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AuthActionTypes.AUTH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        error: null,
      };

    case AuthActionTypes.AUTH_ERROR:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload.error,
      };

    case AuthActionTypes.AUTH_LOGOUT:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null,
      };

    case AuthActionTypes.AUTH_CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AuthActionTypes.AUTH_UPDATE_USER:
      return {
        ...state,
        user: action.payload.user,
      };

    case AuthActionTypes.AUTH_INITIALIZE:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

// Create auth context
const AuthContext = createContext();

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const initializationRef = useRef(false);
  const initPromiseRef = useRef(null);

  // Initialize auth state on app load with deduplication
  useEffect(() => {
    // Prevent multiple initialization attempts
    if (initializationRef.current || initPromiseRef.current) {
      return;
    }

    initializationRef.current = true;

    const initializeAuth = async () => {
      try {
        await authService.initializeAuth();
        
        if (authService.isAuthenticated()) {
          dispatch({
            type: AuthActionTypes.AUTH_INITIALIZE,
            payload: {
              isAuthenticated: true,
              user: authService.getUser(),
            },
          });
        } else {
          dispatch({
            type: AuthActionTypes.AUTH_INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({
          type: AuthActionTypes.AUTH_INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      } finally {
        initPromiseRef.current = null;
      }
    };

    initPromiseRef.current = initializeAuth();
  }, []);

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AuthActionTypes.AUTH_START });
      
      const result = await authService.register(userData);
      
      dispatch({
        type: AuthActionTypes.AUTH_SUCCESS,
        payload: { user: result.user },
      });

      // Track successful registration
      trackAuth('register', 'email');
      trackConversion();

      return { success: true, message: result.message };
    } catch (error) {
      dispatch({
        type: AuthActionTypes.AUTH_ERROR,
        payload: { error: error.message },
      });
      throw error;
    }
  };

  // Login function with complete user data refresh
  const login = async (credentials) => {
    try {
      dispatch({ type: AuthActionTypes.AUTH_START });
      
      const result = await authService.login(credentials);
      
      // After login, we need to fetch the complete user profile including watchlist, favorites, watched arrays
      // The login endpoint only returns basic user info, not the complete profile
      try {
        await authService.getCurrentUser();
      } catch (error) {
        console.warn('Failed to fetch complete user profile after login:', error);
        // Continue anyway with the basic user data from login
      }
      
      dispatch({
        type: AuthActionTypes.AUTH_SUCCESS,
        payload: { user: authService.getUser() },
      });

      // Track successful login
      trackAuth('login', 'email');
      trackConversion({ action: 'login' });

      return { success: true, message: result.message };
    } catch (error) {
      dispatch({
        type: AuthActionTypes.AUTH_ERROR,
        payload: { error: error.message },
      });
      throw error;
    }
  };

  // Login with Google
  const loginWithGoogle = async (credential) => {
    try {
      dispatch({ type: AuthActionTypes.AUTH_START });
      
      const result = await authService.loginWithGoogle(credential);
      
      // Fetch complete user profile
      try {
        await authService.getCurrentUser();
      } catch (error) {
        console.warn('Failed to fetch complete user profile after Google login:', error);
      }
      
      dispatch({
        type: AuthActionTypes.AUTH_SUCCESS,
        payload: { user: authService.getUser() },
      });

      // Track successful Google login
      trackAuth('login', 'google');
      trackGoogleLogin();
      trackConversion({ action: 'login', method: 'google' });

      return { success: true, message: result.message };
    } catch (error) {
      dispatch({
        type: AuthActionTypes.AUTH_ERROR,
        payload: { error: error.message },
      });
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: AuthActionTypes.AUTH_LOGOUT });
      
      // Track logout
      trackAuth('logout', 'email');
    } catch (error) {
      console.error('Logout error:', error);
      // Still logout locally even if server call fails
      dispatch({ type: AuthActionTypes.AUTH_LOGOUT });
      
      // Track logout even if server call fails
      trackAuth('logout', 'email');
    }
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    try {
      const result = await authService.updateProfile(profileData);
      
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: result.user },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      const result = await authService.changePassword(passwordData);
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  // Update user actions to use local state updates when possible
  const addToWatchlist = async (itemId, mediaType) => {
    try {
      const result = await authService.addToWatchlist(itemId, mediaType);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Add to watchlist error:', error);
      throw error;
    }
  };

  const removeFromWatchlist = async (itemId, mediaType) => {
    try {
      const result = await authService.removeFromWatchlist(itemId, mediaType);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Remove from watchlist error:', error);
      throw error;
    }
  };

  const addToFavorites = async (itemId, mediaType) => {
    try {
      const result = await authService.addToFavorites(itemId, mediaType);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Add to favorites error:', error);
      throw error;
    }
  };

  const removeFromFavorites = async (itemId, mediaType) => {
    try {
      const result = await authService.removeFromFavorites(itemId, mediaType);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Remove from favorites error:', error);
      throw error;
    }
  };

  const addToWatched = async (itemId, mediaType, rating = null, comment = '') => {
    try {
      const result = await authService.addToWatched(itemId, mediaType, rating, comment);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Add to watched error:', error);
      throw error;
    }
  };

  const removeFromWatched = async (itemId, mediaType) => {
    try {
      const result = await authService.removeFromWatched(itemId, mediaType);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Remove from watched error:', error);
      throw error;
    }
  };

  const rateItem = async (itemId, mediaType, rating) => {
    try {
      const result = await authService.rateItem(itemId, mediaType, rating);
      
      // Update user in context with the returned data
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: authService.getUser() },
      });

      return { success: true, message: result.message };
    } catch (error) {
      console.error('Rate item error:', error);
      throw error;
    }
  };

  const updateUserPoints = useCallback((points) => {
    if (state.user) {
      dispatch({
        type: AuthActionTypes.AUTH_UPDATE_USER,
        payload: { user: { ...state.user, points } },
      });
    }
  }, [state.user]);

  // Get user statistics function
  const getUserStats = async () => {
    try {
      return await authService.getUserStats();
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  };

  // Clear error function (memoised to keep stable reference)
  const clearError = useCallback(() => {
    dispatch({ type: AuthActionTypes.AUTH_CLEAR_ERROR });
  }, []);

  // Resend email verification
  const resendEmailVerification = async () => {
    try {
      const result = await authService.resendEmailVerification();
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  };

  // Verify email
  const verifyEmail = async (token) => {
    try {
      const result = await authService.verifyEmail(token);
      
      // Refresh user data to update email verification status
      // Only if user is currently authenticated
      if (authService.isAuthenticated()) {
        try {
          await authService.getCurrentUser();
          dispatch({
            type: AuthActionTypes.AUTH_UPDATE_USER,
            payload: { user: authService.getUser() },
          });
        } catch (error) {
          // If getCurrentUser fails, don't fail the entire verification
          // The verification itself was successful
          console.warn('Failed to refresh user data after email verification:', error);
        }
      }
      
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  // Helper functions
  const isInWatchlist = (itemId, mediaType) => {
    return authService.isInWatchlist(itemId, mediaType);
  };

  const isInFavorites = (itemId, mediaType) => {
    return authService.isInFavorites(itemId, mediaType);
  };

  const isWatched = (itemId, mediaType) => {
    return authService.isWatched(itemId, mediaType);
  };

  const getWatchedItem = (itemId, mediaType) => {
    return authService.getWatchedItem(itemId, mediaType);
  };

  const getUserRating = (itemId, mediaType) => {
    return authService.getUserRating(itemId, mediaType);
  };

  // Context value
  const value = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    register,
    login,
    loginWithGoogle,
    logout,
    updateProfile,
    changePassword,
    resendEmailVerification,
    verifyEmail,
    addToWatchlist,
    removeFromWatchlist,
    addToFavorites,
    removeFromFavorites,
    addToWatched,
    removeFromWatched,
    rateItem,
    getUserStats,
    updateUserPoints,
    clearError,

    // Helper functions
    isInWatchlist,
    isInFavorites,
    isWatched,
    getWatchedItem,
    getUserRating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext; 